import {ChangeDetectionStrategy, Component, contentChild, effect, inject, Input, TemplateRef} from '@angular/core';
import {NgxSmartModalComponent} from 'ngx-smart-modal';
import {uniq} from '@matchsource/utils';
import {NgTemplateOutlet} from '@angular/common';
import {DialogService} from '../../services/dialog.service';
import {DIALOG_COMPONENT} from '../../declarations';
import {NgxSmartModalWrapper} from '../../adapters/ngx-smart-modal-adapter/utils';

@Component({
  selector: 'ms-dynamic-dialog',
  templateUrl: './dynamic-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
})
export class DynamicDialogComponent {
  readonly dialogTemplate = contentChild(TemplateRef);
  private readonly dialogComponent = contentChild(DIALOG_COMPONENT);

  @Input()
  set customClass(value: string) {
    this.hostDialog.customClass = value;
  }

  get customClass() {
    return this.hostDialog.customClass;
  }

  get visible() {
    return this.hostDialog.visible;
  }

  @Input()
  set closable(value: boolean) {
    this.hostDialog.closable = value;
  }

  get closable() {
    return this.hostDialog.closable;
  }

  @Input()
  set escapable(value: boolean) {
    this.hostDialog.escapable = value;
  }

  get escapable() {
    return this.hostDialog.escapable;
  }

  public readonly hostDialog = inject(NgxSmartModalComponent);
  public readonly dialogService = inject(DialogService);

  constructor() {
    effect(() => {
      const comp = this.dialogComponent();
      const dialogInstance = this.dialogService.getDialog(this.hostDialog.identifier) as NgxSmartModalWrapper;

      dialogInstance?.setContentComponent(comp);
    });
  }

  appendCustomClass(customClass: string): void {
    // setTimeout is used to avoid ExpressionChangedAfterItHasBeenCheckedError error
    setTimeout(() => {
      const cssClasses: string[] = (this.customClass || '').split(/\s+/);
      cssClasses.push(customClass);

      this.customClass = uniq(cssClasses).join(' ');
      this.markHostDialogForCheck();
    });
  }

  removeCloseOption(): void {
    setTimeout(() => {
      this.closable = false;
      this.markHostDialogForCheck();
    });
  }

  removeEscapableOption(): void {
    setTimeout(() => {
      this.escapable = false;
      this.markHostDialogForCheck();
    });
  }

  /**
   * These setters mutate plain properties on the host `NgxSmartModalComponent` from outside its own
   * change detection, and they run asynchronously (see the `setTimeout` above), so nothing marks
   * that view dirty. It has to be told explicitly, otherwise the change only reaches the DOM when
   * some unrelated event happens to trigger a check -- which is why an appended `customClass` used
   * to appear only as the dialog was being closed.
   */
  private markHostDialogForCheck(): void {
    this.hostDialog.markForCheck();
  }

  getData<T = any>(): T {
    return this.hostDialog.getData() as T;
  }
}
